import { useI18n } from "@common/hooks/i18n";
import { i18nFormat } from "@common/lib/i18n";
import loadable from "@loadable/component";
import React, { Suspense } from "react";
import { type DropzoneOptions, type DropzoneState, type FileRejection } from "react-dropzone";
import OKModal from "../Modal/Modals/OK/component";
import { getMaxSize } from "./lib";

export type OnDropReject = (fileRejections: FileRejection[], maxSizeMb?: number | null) => void;
export type UseDropzone = (opts?: DropzoneOptions) => DropzoneState;

export type WithDropzoneComponentProps = {
  onDropReject: OnDropReject;
  useDropzone: UseDropzone;
};

const DropzoneLib = loadable.lib(() => import(/* webpackChunkName: "react-dropzone" */ "react-dropzone"), {
  fallback: <div style={{ padding: 16 }}>Loading...</div>
});

export default <TProps extends {}>(
    Component: React.ComponentType<TProps & WithDropzoneComponentProps>
  ): React.ComponentType<TProps> =>
  (props: TProps) => {
    const i18n = useI18n();

    const [error, setError] = React.useState<string>();

    const onDropReject = React.useCallback(
      (fileRejections: FileRejection[], maxSizeMb?: number | null) => {
        setError(
          fileRejections
            .map((f) => {
              const error = f.errors
                .map((x) =>
                  x.code === "file-too-large"
                    ? i18nFormat(i18n.fileTooLargeDescription, ["SIZE", getMaxSize(maxSizeMb)])
                    : x.message
                )
                .join("<br/>");
              return `${f.file.name}: ${error[0].toLowerCase() + error.substring(1)}`;
            })
            .join("<br/>")
        );
      },
      [i18n.fileTooLargeDescription]
    );

    return (
      <Suspense>
        <DropzoneLib>
          {(args) => <Component {...props} onDropReject={onDropReject} useDropzone={args.useDropzone} />}
        </DropzoneLib>

        {!!error && (
          <OKModal isVisible onClose={() => setError(undefined)} title={i18n.fileRejected} width={450}>
            <div dangerouslySetInnerHTML={{ __html: error }} />
          </OKModal>
        )}
      </Suspense>
    );
  };
